import * as React from 'react'
import { cn } from '../../lib/utils'
import { accentBg } from './badge-colors'
import { type BadgeColor } from './badge'

/**
 * ColorDot — a small filled dot in one of the badge-palette colors, for use as a
 * leading indicator beside a label (a stage name, a category, a legend entry).
 * It resolves the color through the same `accentBg` map as `Badge`'s status dot,
 * so a standalone dot always matches the badge it represents.
 *
 *   <ColorDot color="blue" />              // md (10px) by default
 *   <span className="flex items-center gap-2"><ColorDot color="emerald" size="sm" /> Won</span>
 *
 * Decorative by default (`aria-hidden`) — the adjacent text carries the meaning.
 * For a *selectable* palette (pick one of many), use `ColorSwatchPicker`.
 */

type ColorDotSize = 'sm' | 'md' | 'lg'

const dotSize: Record<ColorDotSize, string> = {
  sm: 'size-2',
  md: 'size-2.5',
  lg: 'size-3',
}

export interface ColorDotProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Badge-palette color; resolved via the shared `accentBg` map. */
  color: BadgeColor
  /** Dot diameter (default `md` = 10px). */
  size?: ColorDotSize
}

export const ColorDot = React.forwardRef<HTMLSpanElement, ColorDotProps>(
  ({ color, size = 'md', className, ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden="true"
      className={cn(
        'inline-block shrink-0 rounded-full',
        dotSize[size],
        accentBg[color],
        className
      )}
      {...props}
    />
  )
)
ColorDot.displayName = 'ColorDot'
